"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useRouter } from "next/navigation";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

type ImageType = {
  title: string;
  description?: string;
  image: string;
  alt?: string;
  link?: string;
};

type AdGalleryModalProps = {
  images: ImageType[];
  onClose: () => void;
  full?: boolean;
  loading: boolean;
  autoSlide?: boolean;
  slideInterval?: number;
};

const AdGalleryModal = ({
  images,
  onClose,
  full = true,
  loading,
  autoSlide = true,
  slideInterval = 5000,
}: AdGalleryModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoadStates, setImageLoadStates] = useState<{
    [key: number]: boolean;
  }>({});
  const [isPaused, setIsPaused] = useState(false);
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);
  const startX = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-slide functionality
  useEffect(() => {
    if (!autoSlide || images.length <= 1 || isPaused) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, slideInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [images.length, autoSlide, slideInterval, isPaused]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goToPrev();
          break;
        case "ArrowRight":
          e.preventDefault();
          goToNext();
          break;
        case " ":
          e.preventDefault();
          setIsPaused((prev) => !prev);
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Handle outside click
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (startX.current !== null) {
      const deltaX = e.changedTouches[0].clientX - startX.current;
      if (Math.abs(deltaX) > 50) {
        deltaX > 0 ? goToPrev() : goToNext();
      }
    }
    startX.current = null;
    setTimeout(() => setIsPaused(false), 100);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleAdClick = (link: string | undefined) => {
    if (!link) return;
    if (link.startsWith("http")) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      onClose();
      router.push(link);
    }
  };

  const handleImageLoad = (index: number) => {
    setImageLoadStates((prev) => ({ ...prev, [index]: true }));
  };

  const handleImageError = (index: number) => {
    console.warn(`Failed to load image at index ${index}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <SkeletonTheme baseColor="#474A4D" highlightColor="#6b6e72">
          <div className="max-w-4xl w-full mx-4 bg-[#323538] rounded-xl shadow-xl p-6">
            <div className="flex justify-between items-center mb-4">
              <Skeleton height={30} width={200} />
              <Skeleton height={30} width={30} />
            </div>
            <Skeleton height={400} className="rounded-md mb-4" />
            <Skeleton height={20} width="80%" className="mb-2" />
            <Skeleton height={16} width="60%" />
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  // No images state
  if (!images || images.length === 0) {
    return full ? (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-[#323538] text-red-400 p-8 rounded-xl shadow-xl max-w-md mx-4 text-center">
          <p className="text-lg font-medium">لم يتم العثور على إعلانات</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            إغلاق
          </button>
        </div>
      </div>
    ) : null;
  }

  const currentAd = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleOutsideClick}
    >
      <div
        ref={modalRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        className="relative max-w-4xl w-full mx-4 bg-[#323538] rounded-xl shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-600">
          {full && (
            <h2 className="text-xl font-semibold text-white">إعلانات مميزة</h2>
          )}
          <div className="flex items-center gap-2">
            {images.length > 1 && (
              <span className="text-sm text-gray-300">
                {currentIndex + 1} / {images.length}
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors"
            >
              <X size={20} className="text-white" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative">
          {/* Image Display */}
          <div className="relative h-[200px] md:h-[500px] bg-[#202225]">
            {images.map((image, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-all duration-500 ${
                  idx === currentIndex
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-95"
                }`}
              >
                <div
                  onClick={() => handleAdClick(image.link)}
                  className={`w-full h-full relative group ${
                    image.link ? "cursor-pointer" : ""
                  }`}
                >
                  <Image
                    src={image.image}
                    alt={image.alt || image.title || "Advertisement"}
                    fill
                    className="object-contain transition-transform duration-300 group-hover:scale-105"
                    onLoad={() => handleImageLoad(idx)}
                    onError={() => handleImageError(idx)}
                    priority={idx === 0}
                  />

                  {/* Loading overlay */}
                  {!imageLoadStates[idx] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#202225]">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                  )}

                  {/* Link indicator */}
                  {image.link && (
                    <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-black/70 rounded-full p-2">
                        <ExternalLink size={16} className="text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={goToPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <ChevronLeft size={24} className="text-white" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <ChevronRight size={24} className="text-white" />
                </button>
              </>
            )}

            {/* Pause/Play indicator */}
            {autoSlide && images.length > 1 && (
              <div className="absolute top-4 right-4">
                <div
                  className={`w-3 h-3 rounded-full ${
                    isPaused ? "bg-red-500" : "bg-green-500"
                  } opacity-60`}
                ></div>
              </div>
            )}
          </div>

          {/* Ad Information */}
          <div className="p-6 border-t border-gray-600">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white mb-2">
                {currentAd.title}
              </h3>
              {currentAd.description && (
                <p className="text-gray-300 text-sm leading-relaxed">
                  {currentAd.description}
                </p>
              )}
            </div>

            {/* Action Button */}
            {currentAd.link && (
              <button
                onClick={() => handleAdClick(currentAd.link)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
              >
                {currentAd.link.startsWith("http")
                  ? "زيارة الموقع"
                  : "عرض التفاصيل"}
                <ExternalLink size={16} />
              </button>
            )}
          </div>

          {/* Dots Navigation */}
          {images.length > 1 && (
            <div className="absolute bottom-20 left-0 right-0 flex justify-center gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    idx === currentIndex
                      ? "bg-blue-500 scale-125"
                      : "bg-gray-400 hover:bg-gray-300"
                  }`}
                  aria-label={`الانتقال إلى الإعلان ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdGalleryModal;
